import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { AHRadioButton, AHSeparator, AHText, Box, ListItem } from "arkh";
import React, { useLayoutEffect, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { AccountEditScreenProps } from "./AccountEditNavigation";
import { AccountEditRoleScreenQuery } from "./__generated__/AccountEditRoleScreenQuery.graphql";
import {
  useGetRoleDescription,
  useGetRoleLabels,
} from "../UserAdmin/RoleLabels";
import { useNavigationHeaderOptions } from "@/src/common/navigationHeaderOptions";
import { AccountEditRoleScreenSetRoleMutation } from "./__generated__/AccountEditRoleScreenSetRoleMutation.graphql";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";

function InnerAccountEditRoleScreen(
  props: {
    fetchKey: string;
    id: string;
  } & AccountEditScreenProps<"AccountEditRoleScreen">
) {
  const data = useLazyLoadQuery<AccountEditRoleScreenQuery>(
    graphql`
      query AccountEditRoleScreenQuery($id: ID!, $storeId: ID) {
        store(id: $storeId) {
          storeUser(id: $id) @required(action: THROW) {
            id
            role
          }
        }
      }
    `,
    { id: props.id, storeId: props.route.params.storeId },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );
  const [role, setRole] = useState(data.store.storeUser.role);
  const getRoleLabels = useGetRoleLabels();
  const getRoleDescriptions = useGetRoleDescription();

  const [saveRole, saveRoleLoading] =
    useMutation<AccountEditRoleScreenSetRoleMutation>(graphql`
      mutation AccountEditRoleScreenSetRoleMutation(
        $id: ID!
        $role: Roles!
        $storeId: ID
      ) {
        store(id: $storeId) {
          storeUser(id: $id) {
            setRole(role: $role) {
              id
              role
            }
          }
        }
      }
    `);

  const getNavigationHeaderOptions = useNavigationHeaderOptions();

  const intl_tokens = useIntlTokens();
  useLayoutEffect(() => {
    if (role === data.store.storeUser.role) {
      props.navigation.setOptions(
        getNavigationHeaderOptions({
          type: "CLOSE",
          onClose: () => {
            props.navigation.goBack();
          },
          title: intl_tokens.EditRole,
        })
      );
    } else {
      props.navigation.setOptions(
        getNavigationHeaderOptions({
          type: "SAVE",
          onSave: () => {
            saveRole({
              variables: {
                id: props.id,
                role: role,
                storeId: props.route.params.storeId,
              },
              onCompleted: () => {
                props.navigation.goBack();
              },
            });
          },
          onCancel: () => {
            props.navigation.goBack();
          },
          saveLoading: saveRoleLoading,
          title: intl_tokens.EditRole,
        })
      );
    }
  }, [
    role,
    data.store.storeUser.role,
    saveRoleLoading,
    getNavigationHeaderOptions,
    saveRole,
    props.navigation,
    intl_tokens.EditRole,
    props.id,
    props.route.params.storeId,
  ]);

  return (
    <Box style={{ flex: 1 }} color="surface-background-light" space="medium">
      {(["CO_WORKER", "MANAGER", "ADMIN"] as const).map((_role, index) => {
        return (
          <React.Fragment key={_role}>
            {index !== 0 ? <AHSeparator /> : null}
            <ListItem
              isFirst={index === 0}
              border="border-primary"
              isLast={index === 2}
              onPress={() => {
                setRole(_role);
              }}
              disabled={_role === role}
              left={<AHRadioButton selected={role === _role} />}
            >
              <ListItem.Title>{getRoleLabels(_role)}</ListItem.Title>
              <AHText>{getRoleDescriptions(_role)}</AHText>
            </ListItem>
          </React.Fragment>
        );
      })}
    </Box>
  );
}

export function AccountEditRoleScreen(
  props: AccountEditScreenProps<"AccountEditRoleScreen">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <InnerAccountEditRoleScreen
          fetchKey={fetchKey}
          id={props.route.params.id}
          navigation={props.navigation}
          route={props.route}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}
